import { Box, BoxProps, Stack, StackProps } from '@mui/material';
import { isNil } from 'lodash';
import { forwardRef, useMemo } from 'react';
import flattenChildren from 'react-keyed-flatten-children';

import { cardBgLightGrey } from '@/theme';

export type DataReadoutGridProps = {
    columns?: number | 'auto';
};

export const DataReadoutGrid = ({ children, columns, sx, ...props }: DataReadoutGridProps & StackProps) => {
    const childSizingSx = useMemo(() => {
        if (isNil(columns)) {
            return {};
        }
        return {
            boxSizing: 'border-box',
            // When columns=auto, measure ourselves and allocate 360px per column (determined empirically)
            '--column-count':
                columns === 'auto'
                    ? {
                          '@0/data-readout-grid': 1,
                          '@360/data-readout-grid': 2,
                          '@720/data-readout-grid': 3,
                          '@1080/data-readout-grid': 4,
                          '@1440/data-readout-grid': 5,
                          '@1600/data-readout-grid': 6,
                      }
                    : columns,
            // Set childrens' basic width to 1/columnCount, less 2px for each gap
            // Doing it this way is similar to setting minWidth, except that if they grow to fill incomplete rows,
            // they grow by the same amount and stay nice and columny
            flexBasis: 'calc(100% / var(--column-count) - 2px * (var(--column-count) - 1))',
        };
    }, [columns]);

    return (
        <Stack
            direction="row"
            sx={{
                flexWrap: 'wrap',
                gap: 0.5,
                alignItems: 'stretch',
                container: 'data-readout-grid / inline-size',
                '& > *, & > .readout-grid-subcontainer > *': {
                    ...childSizingSx,
                    borderRadius: 0,
                    py: 1.5,
                    px: 1.5,
                    bgcolor: cardBgLightGrey,
                },
                position: 'relative', // so any descendants can tell when the card is on screen
                ...sx,
            }}
            {...props}
        >
            {flattenChildren(children)}
        </Stack>
    );
};

/**
 * If you make this an immediate child of DataReadoutGrid, it will be effectively ignored and its children will get placed in the grid as independent grid members.
 */
export const ReadoutGridSubcontainer = forwardRef(function ReadoutGridSubcontainer(
    { sx, className, ...props }: BoxProps,
    ref
) {
    return (
        <Box
            ref={ref}
            className={`readout-grid-subcontainer ${className ?? ''}`}
            sx={{ display: 'contents', '&:empty': { display: 'none' }, ...sx }}
            {...props}
        />
    );
});
